/**
 * CTAP2 HID Transport Layer
 *
 * Implements the FIDO2 CTAP HID protocol for communicating with USB security keys
 * via the WebHID browser API. This bypasses the browser's WebAuthn API entirely,
 * allowing credential creation for any RP ID (e.g. login.microsoft.com).
 *
 * @see https://fidoalliance.org/specs/fido-v2.1-ps-20210615/fido-client-to-authenticator-protocol-v2.1-ps-errata-20220621.html
 */

// ─── Constants ───────────────────────────────────────────────────────────────

/** FIDO HID usage page for filtering devices */
const FIDO_USAGE_PAGE = 0xf1d0;

/** CTAP HID command identifiers */
const CTAPHID_CBOR = 0x10;
const CTAPHID_INIT = 0x06;
const CTAPHID_ERROR = 0x3f;
const CTAPHID_KEEPALIVE = 0x3b;

/** Broadcast CID used during initialization */
const BROADCAST_CID = new Uint8Array([0xff, 0xff, 0xff, 0xff]);

/** HID report size for FIDO devices (64 bytes) */
const HID_REPORT_SIZE = 64;

/** Init packet payload = 64 - 7 header bytes */
const INIT_PACKET_PAYLOAD = HID_REPORT_SIZE - 7;

/** Continuation packet payload = 64 - 5 header bytes */
const CONT_PACKET_PAYLOAD = HID_REPORT_SIZE - 5;

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Ctap2HidConnection {
  device: HIDDevice;
  channelId: Uint8Array;
}

// ─── WebHID Support Check ────────────────────────────────────────────────────

/** Check if the browser supports WebHID (Chrome/Edge 89+) */
export function isWebHidSupported(): boolean {
  return typeof navigator !== 'undefined' && 'hid' in navigator;
}

// ─── Device Connection ───────────────────────────────────────────────────────

/**
 * Request access to a FIDO2 USB security key via WebHID and open a channel.
 * Shows the browser's device picker dialog.
 */
export async function openFido2Device(): Promise<Ctap2HidConnection> {
  if (!isWebHidSupported()) {
    throw new Error(
      'WebHID is not supported in this browser. Use Chrome or Edge 89+.'
    );
  }

  // Request a FIDO HID device from the user
  const devices = await navigator.hid.requestDevice({
    filters: [{ usagePage: FIDO_USAGE_PAGE }],
  });

  if (devices.length === 0) {
    throw new Error('No FIDO2 security key selected.');
  }

  const device = devices[0];

  if (!device.opened) {
    await device.open();
  }

  // Initialize a CTAP HID channel
  const channelId = await ctapHidInit(device);

  return { device, channelId };
}

/**
 * Close the HID device connection.
 */
export async function closeFido2Device(conn: Ctap2HidConnection): Promise<void> {
  if (conn.device.opened) {
    await conn.device.close();
  }
}

// ─── CTAP HID Init ──────────────────────────────────────────────────────────

/**
 * Perform CTAP HID initialization (allocate a channel ID).
 * Sends CTAPHID_INIT on the broadcast channel with an 8-byte nonce,
 * receives a 4-byte channel ID in response.
 */
async function ctapHidInit(device: HIDDevice): Promise<Uint8Array> {
  const nonce = crypto.getRandomValues(new Uint8Array(8));

  const response = await ctapHidSendReceive(
    device,
    BROADCAST_CID,
    CTAPHID_INIT,
    nonce
  );

  // Response: 8-byte nonce echo + 4-byte channel ID + version info
  if (response.length < 12) {
    throw new Error('Invalid CTAPHID_INIT response (too short)');
  }

  // Verify nonce echo
  for (let i = 0; i < 8; i++) {
    if (response[i] !== nonce[i]) {
      throw new Error('CTAPHID_INIT nonce mismatch');
    }
  }

  // Extract 4-byte channel ID
  return response.slice(8, 12);
}

// ─── CTAP2 CBOR Command ─────────────────────────────────────────────────────

/**
 * Send a CTAP2 CBOR command and receive the response.
 * Prepends the CTAP2 command byte, sends via CTAPHID_CBOR,
 * and returns the CBOR response payload (after stripping the status byte).
 *
 * @param conn - The HID connection
 * @param ctap2Command - CTAP2 command byte (e.g. 0x01 for MakeCredential)
 * @param cborPayload - CBOR-encoded command parameters (may be empty)
 * @returns CBOR response payload (status byte already checked/stripped)
 * @throws Error if the authenticator returns a non-success status
 */
export async function ctap2Command(
  conn: Ctap2HidConnection,
  ctap2Command: number,
  cborPayload: Uint8Array = new Uint8Array(0)
): Promise<Uint8Array> {
  // Build the data: [command_byte] + [cbor_payload]
  const data = new Uint8Array(1 + cborPayload.length);
  data[0] = ctap2Command;
  data.set(cborPayload, 1);

  const response = await ctapHidSendReceive(
    conn.device,
    conn.channelId,
    CTAPHID_CBOR,
    data
  );

  if (response.length === 0) {
    throw new Error('Empty CTAP2 response');
  }

  const status = response[0];
  if (status !== 0x00) {
    throw new Error(`CTAP2 error: 0x${status.toString(16).padStart(2, '0')} (${ctap2ErrorName(status)})`);
  }

  return response.slice(1);
}

// ─── Low-Level HID Framing ───────────────────────────────────────────────────

/**
 * Send a command and receive the full response via CTAP HID framing.
 * Handles initialization packets, continuation packets, and keepalive messages.
 */
async function ctapHidSendReceive(
  device: HIDDevice,
  channelId: Uint8Array,
  command: number,
  data: Uint8Array
): Promise<Uint8Array> {
  // ── Send ──
  await ctapHidSend(device, channelId, command, data);

  // ── Receive ──
  return ctapHidReceive(device, channelId);
}

/**
 * Send a CTAP HID message (fragmented into init + continuation packets).
 */
async function ctapHidSend(
  device: HIDDevice,
  channelId: Uint8Array,
  command: number,
  data: Uint8Array
): Promise<void> {
  const totalLength = data.length;
  let offset = 0;

  // Initialization packet
  const initPacket = new Uint8Array(HID_REPORT_SIZE);
  initPacket.set(channelId, 0);                         // CID (4 bytes)
  initPacket[4] = command | 0x80;                        // CMD with bit 7 set
  initPacket[5] = (totalLength >> 8) & 0xff;             // BCNTH
  initPacket[6] = totalLength & 0xff;                    // BCNTL

  const initPayloadLen = Math.min(totalLength, INIT_PACKET_PAYLOAD);
  initPacket.set(data.slice(offset, offset + initPayloadLen), 7);
  offset += initPayloadLen;

  await device.sendReport(0, initPacket);

  // Continuation packets
  let seq = 0;
  while (offset < totalLength) {
    const contPacket = new Uint8Array(HID_REPORT_SIZE);
    contPacket.set(channelId, 0);                        // CID (4 bytes)
    contPacket[4] = seq & 0x7f;                          // SEQ (0-127)

    const contPayloadLen = Math.min(totalLength - offset, CONT_PACKET_PAYLOAD);
    contPacket.set(data.slice(offset, offset + contPayloadLen), 5);
    offset += contPayloadLen;
    seq++;

    await device.sendReport(0, contPacket);
  }
}

/**
 * Receive a complete CTAP HID response, reassembling fragmented packets.
 * Handles CTAPHID_KEEPALIVE transparently.
 */
function ctapHidReceive(
  device: HIDDevice,
  channelId: Uint8Array
): Promise<Uint8Array> {
  return new Promise((resolve, reject) => {
    let totalLength = 0;
    let buffer: Uint8Array | null = null;
    let offset = 0;
    let expectedSeq = 0;

    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error('CTAP HID response timeout (30s)'));
    }, 30000);

    function cleanup() {
      clearTimeout(timeout);
      device.removeEventListener('inputreport', onInputReport as EventListener);
    }

    function onInputReport(event: HIDInputReportEvent) {
      try {
        const report = new Uint8Array(event.data.buffer);

        // Verify channel ID matches
        const reportCid = report.slice(0, 4);
        if (!arraysEqual(reportCid, channelId)) return; // Different channel, ignore

        if (buffer === null) {
          // Initialization packet
          totalLength = (report[5] << 8) | report[6];

          // Handle keepalive — just wait for more
          if ((report[4] & 0x7f) === CTAPHID_KEEPALIVE) {
            return;
          }

          // Handle error
          if ((report[4] & 0x7f) === CTAPHID_ERROR) {
            cleanup();
            const errorCode = totalLength > 0 ? report[7] : 0;
            reject(new Error(`CTAP HID error: 0x${errorCode.toString(16)}`));
            return;
          }

          buffer = new Uint8Array(totalLength);
          const payloadLen = Math.min(totalLength, INIT_PACKET_PAYLOAD);
          buffer.set(report.slice(7, 7 + payloadLen), 0);
          offset = payloadLen;
        } else {
          // Continuation packet
          const seq = report[4];
          if (seq !== expectedSeq) {
            cleanup();
            reject(new Error(`Unexpected sequence number: got ${seq}, expected ${expectedSeq}`));
            return;
          }
          expectedSeq++;

          const payloadLen = Math.min(totalLength - offset, CONT_PACKET_PAYLOAD);
          buffer.set(report.slice(5, 5 + payloadLen), offset);
          offset += payloadLen;
        }

        // Check if we've received the complete message
        if (buffer !== null && offset >= totalLength) {
          cleanup();
          resolve(buffer);
        }
      } catch (err) {
        cleanup();
        reject(err);
      }
    }

    device.addEventListener('inputreport', onInputReport as EventListener);
  });
}

// ─── Utilities ───────────────────────────────────────────────────────────────

function arraysEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/** Map CTAP2 error codes to human-readable names */
function ctap2ErrorName(code: number): string {
  const names: Record<number, string> = {
    0x01: 'CTAP1_ERR_INVALID_COMMAND',
    0x02: 'CTAP1_ERR_INVALID_PARAMETER',
    0x03: 'CTAP1_ERR_INVALID_LENGTH',
    0x04: 'CTAP1_ERR_INVALID_SEQ',
    0x05: 'CTAP1_ERR_TIMEOUT',
    0x06: 'CTAP1_ERR_CHANNEL_BUSY',
    0x0a: 'CTAP1_ERR_LOCK_REQUIRED',
    0x0b: 'CTAP1_ERR_INVALID_CHANNEL',
    0x11: 'CTAP2_ERR_CBOR_UNEXPECTED_TYPE',
    0x12: 'CTAP2_ERR_INVALID_CBOR',
    0x14: 'CTAP2_ERR_MISSING_PARAMETER',
    0x15: 'CTAP2_ERR_LIMIT_EXCEEDED',
    0x27: 'CTAP2_ERR_UNSUPPORTED_EXTENSION',
    0x29: 'CTAP2_ERR_CREDENTIAL_EXCLUDED',
    0x2c: 'CTAP2_ERR_PROCESSING',
    0x2d: 'CTAP2_ERR_INVALID_CREDENTIAL',
    0x2e: 'CTAP2_ERR_USER_ACTION_PENDING',
    0x2f: 'CTAP2_ERR_OPERATION_PENDING',
    0x30: 'CTAP2_ERR_NO_OPERATIONS',
    0x31: 'CTAP2_ERR_UNSUPPORTED_ALGORITHM',
    0x32: 'CTAP2_ERR_OPERATION_DENIED',
    0x33: 'CTAP2_ERR_KEY_STORE_FULL',
    0x35: 'CTAP2_ERR_NO_OPERATION_PENDING',
    0x36: 'CTAP2_ERR_UNSUPPORTED_OPTION',
    0x37: 'CTAP2_ERR_INVALID_OPTION',
    0x38: 'CTAP2_ERR_KEEPALIVE_CANCEL',
    0x39: 'CTAP2_ERR_NO_CREDENTIALS',
    0x3a: 'CTAP2_ERR_USER_ACTION_TIMEOUT',
    0x3b: 'CTAP2_ERR_NOT_ALLOWED',
    0x3c: 'CTAP2_ERR_PIN_INVALID',
    0x3d: 'CTAP2_ERR_PIN_BLOCKED',
    0x3e: 'CTAP2_ERR_PIN_AUTH_INVALID',
    0x3f: 'CTAP2_ERR_PIN_AUTH_BLOCKED',
    0x40: 'CTAP2_ERR_PIN_NOT_SET',
    0x41: 'CTAP2_ERR_PUAT_REQUIRED',
    0x42: 'CTAP2_ERR_PIN_POLICY_VIOLATION',
    0x44: 'CTAP2_ERR_PIN_TOKEN_EXPIRED',
    0x45: 'CTAP2_ERR_REQUEST_TOO_LARGE',
    0x46: 'CTAP2_ERR_ACTION_TIMEOUT',
    0x47: 'CTAP2_ERR_UP_REQUIRED',
    0x48: 'CTAP2_ERR_UV_BLOCKED',
  };
  return names[code] ?? `UNKNOWN_ERROR_0x${code.toString(16)}`;
}
